import { useState, useEffect, Fragment } from 'react'
import {
    Paper, Box, Typography,
     Checkbox, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, CircularProgress,
    Autocomplete,
    TextField,
    alpha
} from '@mui/material'
import { CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon, CheckBox as CheckBoxIcon } from '@mui/icons-material'
import {
    Check as CheckIcon,
    Close as CancelIcon,
    VisibilityOutlined as ReadIcon,
    EditOutlined as EditIcon
} from '@mui/icons-material'
import { useDispatch } from 'react-redux'
import * as service from 'store/settings/role'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@emotion/react'
import { useSession } from 'components/Providers'

export default function RoleCompare({ availableRoles, selectedRole, districtType }) {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const theme = useTheme()
    const [selectedRoleIds, setSelectedRoleIds] = useState(
      selectedRole ? [selectedRole.id] : []
    )
    const [fullRolesData, setFullRolesData] = useState({})
    const [loading, setLoading] = useState(false)
    const { appearance } = useSession();

    useEffect(() => {
      if (selectedRole && !selectedRoleIds.includes(selectedRole.id)) {
        setSelectedRoleIds(prev => [selectedRole.id, ...prev])
      }
    }, [selectedRole?.id])

    useEffect(() => {
        const fetchMissingRoles = async () => {
            const missingIds = selectedRoleIds.filter(id => !fullRolesData[id])
            if (missingIds.length === 0) return

            setLoading(true)
            const newData = { ...fullRolesData }

            for (let id of missingIds) {
                const { error, payload } = await dispatch(service.get({ id: parseInt(id) }))
                if (!error && payload) {
                    newData[id] = payload
                }
            }

            setFullRolesData(newData)
            setLoading(false)
        }

        fetchMissingRoles()
    }, [selectedRoleIds, dispatch, fullRolesData])

    const masterModules = selectedRoleIds.length > 0 && fullRolesData[selectedRoleIds[0]]
        ? fullRolesData[selectedRoleIds[0]].modules.filter(module => module.types?.includes(districtType))
        : []

    const getPermissionValue = (roleId, moduleId, permId) => {
        const role = fullRolesData[roleId]
        if (!role || !role.modules) return null

        const mod = role.modules.find(m => m.id === moduleId && m.types?.includes(districtType))
        if (!mod) return null

        const perm = mod.permissions.find(p => p.id === permId)
        return perm ? perm.value : null
    }

    const renderBadge = (value, type) => {
        if (value === null || value === undefined) return <Typography variant="caption" color="text.disabled">—</Typography>

        if (type === 'model' || type === 'xmodel') {
            if (value === 15) return <Chip size="small" color="primary" icon={<EditIcon />} label="Full Control" />
            if (value === 1) return <Chip size="small" color="info" icon={<ReadIcon />} label="Read Only" />
            return <Chip size="small" color="default" icon={<CancelIcon />} label="No Access" sx={{ opacity: 0.6 }} />
        }

        if (value) return <Chip size="small" color="success" icon={<CheckIcon />} label="Enabled" />
        return <Chip size="small" color="default" icon={<CancelIcon />} label="Disabled" sx={{ opacity: 0.6 }} />
    }

    return (
        <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 3, bgcolor: alpha(theme.palette.background.default, 0.3), borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight="700" mb={1}>
                    {t('Compare Roles')}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                    {t('Select up to 4 roles to compare their module permissions side-by-side.')}
                </Typography>

                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={availableRoles}
                    getOptionLabel={(option) => option.name}
                    value={availableRoles.filter(role => selectedRoleIds.includes(role.id))}
                    onChange={(event, newValue) => {
                        const newIds = newValue.map(role => role.id)
                        if (newIds.length === 0 || (newIds.length <= 4 && (!selectedRole || newIds.includes(selectedRole.id)))) {
                            setSelectedRoleIds(newIds)
                        }
                    }}
                    sx={{ width: '100%', maxWidth: 600 }}
                    renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Checkbox
                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                style={{ marginRight: 8, padding: 0, marginTop: 2 }}
                                checked={selected}
                            />
                            <Box>
                                <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {option.districtType}
                                </Typography>
                            </Box>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('Select Roles to Compare')}
                            placeholder={selectedRoleIds.length < 4 ? t('Search roles...') : t('Maximum 4 roles reached')}
                        />
                    )}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={option.id}
                                label={option.name}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        ))
                    }
                />
            </Box>

            {selectedRoleIds.length > 0 ? (
                <TableContainer >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '30%', bgcolor: appearance === 'dark' ? 'grey.800' : 'grey.100', fontWeight: 'bold' }}>
                                    {t('Module / Permission')}
                                </TableCell>
                                {selectedRoleIds.map(id => (
                                    <TableCell key={id} align="center" sx={{ bgcolor: appearance === 'dark' ? 'grey.800' : 'grey.100', fontWeight: 'bold', minWidth: 150 }}>
                                        {availableRoles.find(r => r.id === id)?.name}
                                        {loading && !fullRolesData[id] && <CircularProgress size={16} sx={{ ml: 1 }} />}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {masterModules.map(module => (
                                <Fragment key={module.id}>
                                    <TableRow>
                                        <TableCell
                                            colSpan={selectedRoleIds.length + 1}
                                            sx={{ bgcolor: 'primary.50', color: 'primary.900', fontWeight: 700, py: 1.5 }}
                                        >
                                            {module.name}
                                        </TableCell>
                                    </TableRow>

                                    {module.permissions.map(perm => (
                                        <TableRow key={perm.id} hover>
                                            <TableCell sx={{ pl: 4 }}>
                                                <Typography variant="body2" fontWeight={600}>{perm.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{perm.description}</Typography>
                                            </TableCell>

                                            {selectedRoleIds.map(id => (
                                                <TableCell key={id} align="center" >
                                                    {fullRolesData[id] ? (
                                                        renderBadge(getPermissionValue(id, module.id, perm.id), perm.type)
                                                    ) : (
                                                        <Typography variant="caption" color="text.disabled">Loading...</Typography>
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        {t('Please select at least one role from the dropdown above to begin comparison.')}
                    </Typography>
                </Box>
            )}
        </Paper>
    )
}